import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Application,
  AppRole,
  RequiredResourceAccess,
  ResourceAccess,
} from "@microsoft/microsoft-graph-types";
import { useMsal } from "@azure/msal-react";
import { Client } from "@microsoft/microsoft-graph-client";
import "./AppTester.css";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { PublicClientApplication, AccountInfo } from "@azure/msal-browser";
import {
  Typography,
  Button,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  ListItemButton,
  Tooltip,
} from "@mui/material";
import ClientCredentialsManager from "./ClientCredentialsManager";
import CapApplied from "./CapApplied";
// import { SelectChangeEvent } from "@mui/material";
import ApiPermissionsManager from "./ApiPermissionsManager";
import GeneralSettings from "./GeneralSettings";
import OptionalClaims from "./OptionalClaims";
import AppRoles from "./AppRoles";
import UserProvisioning from "./UserProvisioning";
import Owners from "./Owners";
import AuthenticateAs from "./AuthenticateAs";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppSettingsAltIcon from '@mui/icons-material/AppSettingsAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
// Add UI component imports
import { ListItemIcon } from '@mui/material';

// Add icon imports
import SettingsIcon from '@mui/icons-material/Settings';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BadgeIcon from '@mui/icons-material/Badge';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SecurityIcon from '@mui/icons-material/Security';
import ApiIcon from '@mui/icons-material/Api'; 
import TimerIcon from '@mui/icons-material/Timer';
import ConditionalAccessInfo from './ConditionalAccessInfo';
import ServicePrincipalOverview from './ServicePrincipalOverview'; 
import { AlertTitle } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business'; 
import TokenIcon from '@mui/icons-material/Token';
import { useAppCache } from '../context/AppCacheContext';

interface AppTesterProps {
  app: Application;
  onBack: () => void;
  onDecodedTokenTested?: (decoded: any) => void; // callback to parent
}

interface TestUserSession {
  testedMsalInstance: PublicClientApplication | null;
  testerAccount: AccountInfo | null;
  rawIdToken: string | null;
  rawAccessToken?: string | null;
}

const AppTester: React.FC<AppTesterProps> = ({ app, onBack, onDecodedTokenTested }) => {
  /**
   * ============================
   * STATES & HOOKS
   * ============================
   */
  const { instance, accounts } = useMsal();

  // Testing authentication states
  const [servicePrincipalId, setServicePrincipalId] = useState<string | null>(null);
  const [spLoading, setSpLoading] = useState<boolean>(false);
  // States for original and modified app
  const [modifiedApp, setModifiedApp] = useState<Application>({ ...app });
  const [originalApp, setOriginalApp] = useState<Application>({ ...app });
  const [testUserSession, setTestUserSession] = useState<TestUserSession>({
    testedMsalInstance: null,
    testerAccount: null,
    rawIdToken: null,
    rawAccessToken: null
  });

  // For ClientCredentialsManager & PlatformManager
  const [clientCredsModalOpen, setClientCredsModalOpen] = useState(false);
  const [mainAccessToken, setMainAccessToken] = useState("");

  // Determine platform
  const isSpa = Boolean(app.spa?.redirectUris && app.spa.redirectUris.length > 0);
  const isWeb = Boolean(app.web?.redirectUris && app.web.redirectUris.length > 0);

  // App Roles, Owners
  const [appRoles, setAppRoles] = useState<AppRole[]>(app.appRoles || []);

  // File input for Backup/Restore
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // States for claims mapping policies
  const [claimsMappingPolicies, setClaimsMappingPolicies] = useState<any[]>([]);
  const [appClaimsMappingPolicy, setAppClaimsMappingPolicy] = useState<any>(null);

  // NEW: Cache hook
  const { currentTenantId } = useAppCache();
  
  // Acquire main token for Graph
  useEffect(() => {
    instance
      .acquireTokenSilent({
        scopes: ["Application.Read.All"],
        account: accounts[0],
      })
      .then((resp) => {
        setMainAccessToken(resp.accessToken);
      })
      .catch((err) => console.error("Failed to get token:", err));
  }, [instance, accounts]);

  // -------------
  // Side Nav: which section is selected
  // -------------
  const [selectedSection, setSelectedSection] = useState("General Settings");

  // -------------
  // GET /servicePrincipals for app
  // -------------
  useEffect(() => {
    if (!app.appId) return;
    const fetchServicePrincipal = async () => {
      setSpLoading(true);
      const accessTokenRequest = {
        scopes: ["Application.Read.All"],
        account: accounts[0],
      };

      try {
        const response = await instance.acquireTokenSilent(accessTokenRequest);
        const client = Client.init({
          authProvider: (done) => done(null, response.accessToken),
        });
        const spRes = await client
          .api(`/servicePrincipals?$filter=appId eq '${app.appId}'`)
          .get();

        if (spRes.value && spRes.value.length > 0) {
          setServicePrincipalId(spRes.value[0].id);
        } else {
          setServicePrincipalId(null);
        }
      } catch (error) {
        console.error("Error fetching service principal:", error);
        setServicePrincipalId(null);
      } finally {
        setSpLoading(false);
      }
    };
    fetchServicePrincipal();
  }, [app.appId, instance, accounts]);


  // Note: Removed unused helpers related to write operations and policy fetches


  function handleCopyToClipboard(text: string): void {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        alert("Copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
      });
  }

  function handleBackupJsonLoaded(backupObj: any) {
    setModifiedApp(backupObj);
  }

  // ----------------------------------------------------------------
  // RENDER THE SECTION CONTENT
  // ----------------------------------------------------------------
  function renderSectionContent() {
    switch (selectedSection) {
      // 1) BACKUP / RESTORE

        
      // 2) GENERAL SETTINGS - READ-ONLY
      case "General Settings":
        return (
          <GeneralSettings
          app={app}
          modifiedApp={modifiedApp}
          setModifiedApp={setModifiedApp}
          spLoading={spLoading}
          servicePrincipalId={servicePrincipalId}
          tenantId={accounts[0]?.tenantId || ""}
          handleCopyToClipboard={handleCopyToClipboard}
          setClientCredsModalOpen={setClientCredsModalOpen}
          // REMOVED: updateApplication, graphAccessToken, onAppUpdated - not needed in read-only mode
        />
        );
        
      // 3) OPTIONAL CLAIMS
      case "Optional Claims":
        return (
          <OptionalClaims
            modifiedApp={modifiedApp}
          />
        );

      // 4) APP ROLES
      case "App Roles":
        return (
              <AppRoles 
                appRoles={appRoles}            // the array of roles in parent state
                appId={app.appId || ""}
              />
        );

      // 5) API PERMISSIONS
      case "API Permissions":
        return (
          <ApiPermissionsManager
            appId={app.id || ""}
            clientAppId={app.appId || ""}
            clientServicePrincipalId={servicePrincipalId || ""}
            initialRequiredResourceAccess={
              (app.requiredResourceAccess || []).map((graphRra) => ({
                resourceAppId: graphRra.resourceAppId || "",
                resourceAccess: (graphRra.resourceAccess || []).map((ra) => ({
                  id: ra.id || "",
                  type: ra.type === "Scope" ? "Scope" : "Role",
                })),
              }))
            }
          />
        );
      

      // 6) USER PROVISIONING
      case "User Provisioning":
        return (
          <UserProvisioning
            appId={app.appId!}
            servicePrincipalId={servicePrincipalId || undefined}
          />
        );

      // 7) OWNERS
      case "Owners":
        return (
          <Owners
            appId={app.id || ""}
            instance={instance}
            accounts={accounts}
          />
        );

      // 8) CAP Applied
      case "CAP Applied":
        return (
              <CapApplied app={app} />
        );


        // 9) AUTHENTICATE AS 
        case "Authenticate as":
          return (
            <AuthenticateAs
              app={app}
              onDecodedTokenTested={onDecodedTokenTested}
              testUserSession={testUserSession}
              setTestUserSession={setTestUserSession}
            />
          );     

        // 10) Session Management
        case "Session Management":
          return (
                <ConditionalAccessInfo application={app} />
          );

        // 11) Service Principal Overview
        case "Service Principal":
          return (
            <ServicePrincipalOverview
              servicePrincipalId={servicePrincipalId}
              graphAccessToken={mainAccessToken}
              appDisplayName={app.displayName ?? undefined} 
            />
          );

      default:
        return (
          <Typography variant="h6">
            Select a section from the left menu.
          </Typography>
        );
    }
  }

  // ----------------------------------------------------------------
  // MAIN RETURN
  // ----------------------------------------------------------------
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* TOP BAR: Enhanced design with better spacing and visual hierarchy */}
      <Box
        sx={{
          position: "relative",
          py: 2,
          px: 3,
          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          backgroundColor: "#f8f9fc",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
        }}
      >
        {/* Left side - Exit button with improved styling */}
        <Button
          variant="outlined"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          size="medium"
          sx={{ 
            fontWeight: 500, 
            borderRadius: 2,
            pl: 1.5,
            color: "text.secondary",
            borderColor: "divider",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.04)",
              borderColor: "primary.main",
              color: "primary.main"
            }
          }}
        >
          Exit
        </Button>
        
        {/* Center - App title with improved typography */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)'
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              fontWeight: 500,
              letterSpacing: "-0.01em",
              color: "text.primary",
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <AppSettingsAltIcon sx={{ mr: 1, color: 'primary.main' }} />
            Tested App:
            <Typography 
              component="span" 
              sx={{ 
                ml: 1,
                color: "primary.main",
                fontWeight: 600,
                fontSize: "inherit",
                fontFamily: "inherit"
              }}
            >
              {app.displayName}
            </Typography>
          </Typography>
        </Box>
        
        {/* Right side - Optional actions area */}
        <Box>
          <Tooltip title="App ID">
            <Typography 
              variant="caption" 
              sx={{ 
                color: "text.secondary",
                cursor: "pointer",
                "&:hover": { color: "primary.main" }
              }}
              onClick={() => handleCopyToClipboard(app.appId || "")}
            >
              ID: {app.appId?.substring(0, 8)}...
              <ContentCopyIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
            </Typography>
          </Tooltip>
        </Box>
      </Box>

      {/* MAIN AREA: side nav on the left, content on the right */}
      <Box sx={{ display: "flex", flexGrow: 1, height: "100%" }}>
        {/* Side menu with category separation - Enhanced for Testing prominence */}
        <Box
          sx={{
            width: 240,
            backgroundColor: "#f5f7fa",
            flexShrink: 0,
            borderRight: "1px solid rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            height: "100%"
          }}
        >

    {/* Configuration Category - Moved down */}
    <Typography 
      variant="overline" 
      sx={{ 
        px: 2, 
        pt: 1,
        pb: 1, 
        fontWeight: 600, 
        color: 'text.secondary',
        letterSpacing: '0.08em'
      }}
    >
      Configuration
    </Typography>
    <List dense disablePadding>
      {/* Existing configuration items - unchanged */}
      <ListItem disablePadding>
        <ListItemButton
          selected={selectedSection === "General Settings"}
          onClick={() => setSelectedSection("General Settings")}
          sx={{
            borderLeft: selectedSection === "General Settings" ? 3 : 0,
            borderLeftColor: "primary.main",
            pl: selectedSection === "General Settings" ? 1.7 : 2
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SettingsIcon 
              fontSize="small" 
              sx={{ color: selectedSection === "General Settings" ? "primary.main" : "text.secondary" }} 
            />
          </ListItemIcon>
          <ListItemText 
            primary="General Settings" 
            primaryTypographyProps={{ 
              fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", 
              fontWeight: selectedSection === "General Settings" ? 600 : 400,
              fontSize: "0.875rem"
            }} 
          />
        </ListItemButton>
      </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedSection === "Optional Claims"}
              onClick={() => setSelectedSection("Optional Claims")}
              sx={{
                borderLeft: selectedSection === "Optional Claims" ? 3 : 0,
                borderLeftColor: "primary.main",
                pl: selectedSection === "Optional Claims" ? 1.7 : 2
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AssignmentIcon 
                  fontSize="small" 
                  sx={{ color: selectedSection === "Optional Claims" ? "primary.main" : "text.secondary" }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary="Optional Claims" 
                primaryTypographyProps={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", 
                  fontWeight: selectedSection === "Optional Claims" ? 600 : 400,
                  fontSize: "0.875rem"
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedSection === "App Roles"}
              onClick={() => setSelectedSection("App Roles")}
              sx={{
                borderLeft: selectedSection === "App Roles" ? 3 : 0,
                borderLeftColor: "primary.main",
                pl: selectedSection === "App Roles" ? 1.7 : 2
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BadgeIcon 
                  fontSize="small" 
                  sx={{ color: selectedSection === "App Roles" ? "primary.main" : "text.secondary" }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary="App Roles" 
                primaryTypographyProps={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", 
                  fontWeight: selectedSection === "App Roles" ? 600 : 400,
                  fontSize: "0.875rem"
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedSection === "API Permissions"}
              onClick={() => setSelectedSection("API Permissions")}
              sx={{
                borderLeft: selectedSection === "API Permissions" ? 3 : 0,
                borderLeftColor: "primary.main",
                pl: selectedSection === "API Permissions" ? 1.7 : 2
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <VpnKeyIcon 
                  fontSize="small" 
                  sx={{ color: selectedSection === "API Permissions" ? "primary.main" : "text.secondary" }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary="API Permissions" 
                primaryTypographyProps={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", 
                  fontWeight: selectedSection === "API Permissions" ? 600 : 400,
                  fontSize: "0.875rem"
                }} 
              />
            </ListItemButton>
          </ListItem>       
          
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedSection === "User Provisioning"}
              onClick={() => setSelectedSection("User Provisioning")}
              sx={{
                borderLeft: selectedSection === "User Provisioning" ? 3 : 0,
                borderLeftColor: "primary.main",
                pl: selectedSection === "User Provisioning" ? 1.7 : 2
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <PersonAddIcon 
                  fontSize="small" 
                  sx={{ color: selectedSection === "User Provisioning" ? "primary.main" : "text.secondary" }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary="User Provisioning" 
                primaryTypographyProps={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", 
                  fontWeight: selectedSection === "User Provisioning" ? 600 : 400,
                  fontSize: "0.875rem"
                }} 
              />
            </ListItemButton>
          </ListItem>
          
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedSection === "Owners"}
              onClick={() => setSelectedSection("Owners")}
              sx={{
                borderLeft: selectedSection === "Owners" ? 3 : 0,
                borderLeftColor: "primary.main",
                pl: selectedSection === "Owners" ? 1.7 : 2
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <AdminPanelSettingsIcon 
                  fontSize="small" 
                  sx={{ color: selectedSection === "Owners" ? "primary.main" : "text.secondary" }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary="Owners" 
                primaryTypographyProps={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", 
                  fontWeight: selectedSection === "Owners" ? 600 : 400,
                  fontSize: "0.875rem"
                }} 
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Divider sx={{ my: 1.5 }} />

        {/* Information Category */}
        <Typography 
          variant="overline" 
          sx={{ 
            px: 2, 
            pt: 1,
            pb: 1, 
            fontWeight: 600, 
            color: 'text.secondary',
            letterSpacing: '0.08em'
          }}
        >
          Information
        </Typography>
        <List dense disablePadding>
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedSection === "CAP Applied"}
              onClick={() => setSelectedSection("CAP Applied")}
              sx={{
                borderLeft: selectedSection === "CAP Applied" ? 3 : 0,
                borderLeftColor: "primary.main",
                pl: selectedSection === "CAP Applied" ? 1.7 : 2
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <SecurityIcon 
                  fontSize="small" 
                  sx={{ color: selectedSection === "CAP Applied" ? "primary.main" : "text.secondary" }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary="CAP Applied" 
                primaryTypographyProps={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", 
                  fontWeight: selectedSection === "CAP Applied" ? 600 : 400,
                  fontSize: "0.875rem"
                }} 
              />
            </ListItemButton>
          </ListItem>

          {/* Add new ListItemButton for Service Principal Overview */}
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedSection === "Service Principal"}
                onClick={() => setSelectedSection("Service Principal")}
                sx={{
                  borderLeft: selectedSection === "Service Principal" ? 3 : 0,
                  borderLeftColor: "primary.main",
                  pl: selectedSection === "Service Principal" ? 1.7 : 2
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <BusinessIcon // Using BusinessIcon for Service Principal
                    fontSize="small"
                    sx={{ color: selectedSection === "Service Principal" ? "primary.main" : "text.secondary" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Service Principal"
                  primaryTypographyProps={{
                    fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                    fontWeight: selectedSection === "Service Principal" ? 600 : 400,
                    fontSize: "0.875rem"
                  }}
                />
              </ListItemButton>
            </ListItem>

           {/* Azure Best Practice: Add Session Management information section */}
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedSection === "Session Management"}
              onClick={() => setSelectedSection("Session Management")}
              sx={{
                borderLeft: selectedSection === "Session Management" ? 3 : 0,
                borderLeftColor: "primary.main",
                pl: selectedSection === "Session Management" ? 1.7 : 2
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <TimerIcon 
                  fontSize="small" 
                  sx={{ color: selectedSection === "Session Management" ? "primary.main" : "text.secondary" }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary="Session Management" 
                primaryTypographyProps={{ 
                  fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", 
                  fontWeight: selectedSection === "Session Management" ? 600 : 400,
                  fontSize: "0.875rem"
                }} 
              />
            </ListItemButton>
          </ListItem>
        </List>

        <Divider sx={{ my: 1.5 }} />

           {/* TESTING CATEGORY - NEW: Moved to top for prominence */}
          <Typography 
            variant="overline" 
            sx={{ 
              px: 2, 
              pt: 1,
              pb: 1, 
              fontWeight: 600, 
              color: 'text.secondary',
              letterSpacing: '0.08em'
            }}
          >
            Testing
          </Typography>
          <List dense disablePadding>
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedSection === "Authenticate as"}
                onClick={() => setSelectedSection("Authenticate as")}
                sx={{
                  borderLeft: selectedSection === "Authenticate as" ? 4 : 0, // Thicker border
                  borderLeftColor: "#1976d2", // Blue instead of primary
                  pl: selectedSection === "Authenticate as" ? 1.5 : 2,
                  backgroundColor: selectedSection === "Authenticate as" 
                    ? 'rgba(25, 118, 210, 0.08)' // Light blue background when selected
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    transform: 'translateX(2px)', // Subtle slide effect
                    transition: 'all 0.2s ease'
                  },
                  minHeight: 48, // Taller than other items
                  mb: 0.5 // Add margin bottom for spacing
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}> {/* Slightly wider for prominence */}
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    backgroundColor: selectedSection === "Authenticate as" ? '#1976d2' : '#e3f2fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    boxShadow: selectedSection === "Authenticate as" 
                      ? '0 4px 12px rgba(25, 118, 210, 0.3)' 
                      : '0 2px 4px rgba(25, 118, 210, 0.1)'
                  }}>
                    <TokenIcon 
                      fontSize="small" 
                      sx={{ 
                        color: selectedSection === "Authenticate as" ? "white" : "#1976d2",
                        fontSize: '1.1rem'
                      }} 
                    />
                  </Box>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{
                        fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif", 
                        fontWeight: selectedSection === "Authenticate as" ? 700 : 600, // Bolder text
                        fontSize: "0.9rem", // Slightly larger
                        color: selectedSection === "Authenticate as" ? '#1976d2' : '#1565c0'
                      }}>
                        Test Authentication
                      </Typography>
                      {/* Add a "live" indicator badge */}
                      <Box sx={{
                        ml: 1,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#4caf50',
                        animation: selectedSection === "Authenticate as" 
                          ? 'pulse 2s infinite' 
                          : 'none'
                      }} />
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontStyle: 'italic'
                    }}>
                      Get access tokens
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          </List>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(25, 118, 210, 0.2)' }} />        
        {/* Bottom spacer */}
        <Box sx={{ flexGrow: 1 }} />
      </Box>        
        <Box sx={{ flexGrow: 1, p: 0, ml: 2.5, overflowY: "auto" }}> {/* Slightly more margin */}
          {renderSectionContent()}
        </Box>

        {/* Also the two modals below, so they’re not lost. */}
        <ClientCredentialsManager
          open={clientCredsModalOpen}
          onClose={() => setClientCredsModalOpen(false)}
          app={modifiedApp}
          graphAccessToken={mainAccessToken}
          onSecretCreated={(secretInfo) => {
            console.log("New secret created!", secretInfo);
          }}
        />
      </Box>
    </Box>
  );
};

export default AppTester;

export {};
